import {useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';

import {toClassroomIds} from 'client/TeacherDashboard/SkillPaths/hooks/skillPathEntryFilters';
import {SkillRecommendationSortField, SkillRecommendationStatus, SortOrder} from 'gql/types';
import type {SkillRecommendationFilterInput} from 'gql/types';
import {normalizeError} from 'lib/normalizeError';

import {forgetReplaced, getHandledIds, subscribeToHandled} from '../api/mockApi';
import {useSkillRecommendationsAsEducatorQuery} from '../queries/SkillRecommendations.queries.generated';
import type {
  SkillRecommendationFieldsFragment,
  SkillRecommendationsAsEducatorQuery
} from '../queries/SkillRecommendations.queries.generated';
import type {Recommendation} from '../types';

import {useClassroomNames} from './useClassroomNames';

/** The read's own ceiling (Ash `max_page_size`); a larger `first` is rejected, not truncated. */
export const PAGE_SIZE = 250;

/** Students' lines come back in path order; `groupRecommendations` keeps that order inside each group. */
export const INBOX_SORT = [{field: SkillRecommendationSortField.Position, order: SortOrder.Asc}];

const NO_ROWS: SkillRecommendationFieldsFragment[] = [];

/** Proposed recommendations, in one class or (null) every active class. */
export const inboxFilter = (classroomIds: string[] | null): SkillRecommendationFilterInput => ({
  status: {eq: SkillRecommendationStatus.Proposed},
  ...(classroomIds && {classroomId: {in: classroomIds}})
});

/** The next page appended to the rows so far. An edit between pages can repeat a row. */
const appendPage = (
  previous: SkillRecommendationsAsEducatorQuery,
  next: SkillRecommendationsAsEducatorQuery
): SkillRecommendationsAsEducatorQuery => {
  const soFar = previous.skillRecommendationsAsEducator;
  const page = next.skillRecommendationsAsEducator;
  if (!soFar || !page) return next;
  const seen = new Set(soFar.results?.map(({id}) => id));
  return {
    ...next,
    skillRecommendationsAsEducator: {
      ...page,
      results: [...(soFar.results ?? []), ...(page.results ?? []).filter(({id}) => !seen.has(id))]
    }
  };
};

export interface RecommendedSkillsResult {
  /** Every proposed recommendation in scope, with its class named, once every page is in. */
  recommendations: Recommendation[];
  /** True until a complete list is on hand; a revisit keeps the last complete list while it re-pages. */
  isLoading: boolean;
  /** The first page failing, or a later one; `refetch` starts over from the first page. */
  error: Error | undefined;
  refetch: () => void;
}

/**
 * The Recommended inbox for one class or every active class. Follows the keyset until every page
 * is in, names classes through the classroom store, and hides what the mock approve already took.
 */
export const useRecommendedSkills = (classroomId?: string): RecommendedSkillsResult => {
  const classroomIds = useMemo(() => toClassroomIds(classroomId), [classroomId]);
  const filter = useMemo(() => inboxFilter(classroomIds), [classroomIds]);

  const {data, loading, error, refetch, fetchMore} = useSkillRecommendationsAsEducatorQuery({
    variables: {filter, sort: INBOX_SORT, first: PAGE_SIZE},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  const page = data?.skillRecommendationsAsEducator;
  const rows = page?.results ?? NO_ROWS;
  const total = page?.count ?? rows.length;
  const nextCursor = data && rows.length < total ? (page?.endKeyset ?? null) : null;

  // A later page that failed stops the paging at its cursor; it is surfaced through `error` and
  // only `refetch` clears it.
  const [pageError, setPageError] = useState<Error>();
  const failedCursor = useRef<string | null>(null);
  // Pages are keyed to the refetch they belong to, so one landing after a refetch is dropped and
  // the refetch's own paging picks its rows up.
  const generation = useRef(0);
  // One request per cursor and generation: `loading` covers the in-flight page, and the ref covers
  // the render between its settling and the merged data arriving.
  const inFlight = useRef<string | null>(null);
  useEffect(() => {
    const started = generation.current;
    const key = `${started}:${nextCursor}`;
    if (!nextCursor || loading || inFlight.current === key || failedCursor.current === nextCursor) {
      return;
    }
    inFlight.current = key;
    fetchMore({
      variables: {after: nextCursor},
      updateQuery: (previous, {fetchMoreResult}) =>
        generation.current === started ? appendPage(previous, fetchMoreResult) : previous
    })
      .catch((fetchMoreError: unknown) => {
        failedCursor.current = nextCursor;
        setPageError(new Error(normalizeError(fetchMoreError).message));
      })
      .finally(() => {
        if (inFlight.current === key) inFlight.current = null;
      });
  }, [fetchMore, loading, nextCursor]);

  const isComplete = Boolean(data) && !nextCursor && !loading;
  useEffect(() => {
    if (isComplete) forgetReplaced({classroomIds}, rows);
  }, [classroomIds, isComplete, rows]);

  // The last complete list, shown while a revisit's revalidation re-pages from the first page.
  // An explicit refetch (generate, approve) starts over and shows the skeleton instead.
  const [lastComplete, setLastComplete] = useState<SkillRecommendationFieldsFragment[] | null>(
    null
  );
  if (data && !nextCursor && lastComplete !== rows) setLastComplete(rows);
  const shown = nextCursor && lastComplete ? lastComplete : rows;

  const names = useClassroomNames();
  // What the mock approve already took, read as external state so the list follows it.
  const handledIds = useSyncExternalStore(subscribeToHandled, getHandledIds);
  const recommendations = useMemo(
    () =>
      shown
        .filter(({id}) => !handledIds.has(id))
        .map((row) => ({
          ...row,
          classroom: {id: row.classroomId, name: names.get(row.classroomId) ?? 'Class'}
        })),
    [handledIds, names, shown]
  );

  return {
    recommendations,
    isLoading: !data || (Boolean(nextCursor) && !pageError && !lastComplete),
    error: error ?? pageError,
    refetch: () => {
      generation.current += 1;
      inFlight.current = null;
      failedCursor.current = null;
      setLastComplete(null);
      setPageError(undefined);
      // Failures surface through `error`.
      void refetch().catch(() => {});
    }
  };
};
